/*
 * kp-notify-me.js — the PDP's sold-out panel (ticket #19).
 *
 * One custom element, two jobs:
 *
 *   1. Show and hide with Dawn's own verdict. product-info.js disables the
 *      submit button and writes "Sold out" into it for a sold-out variant,
 *      and re-enables it when the customer picks one that is in stock. This
 *      element watches that button and follows — it never decides
 *      availability itself. "Unavailable" (a combination that does not
 *      exist) also disables the button, but with a different label, and gets
 *      no panel: there is no variant to wait for.
 *
 *   2. Sign the customer up with Klaviyo's Client API, from the browser,
 *      with the PUBLIC company id only. Two requests, because they do two
 *      things: back-in-stock-subscriptions records which VARIANT they are
 *      waiting for (that is what a flow or a segment keys on), and
 *      subscriptions records marketing consent on the list, without which a
 *      manual campaign cannot reach them. 409 from either means "already
 *      subscribed", which is success as far as the customer is concerned.
 *
 * Nothing here touches the cart (hard rule 2). Nothing here computes stock.
 *
 * Wrapped in an IIFE: every kp-*.js is a classic <script src>, and a page
 * can carry the same file twice (ticket #22).
 */
(() => {
  const BACK_IN_STOCK_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions/';
  // Klaviyo versions its API by date. Bump deliberately, with the docs open.
  const API_REVISION = '2025-01-15';
  // Klaviyo's id for a Shopify variant: integration:::catalog:::externalId.
  const VARIANT_ID_PREFIX = '$shopify:::$default:::';
  const STORAGE_PREFIX = 'kp-notify:';

  class KpNotifyMe extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-kp-notify-form]');
      this.email = this.querySelector('[data-kp-notify-email]');
      this.phone = this.querySelector('[data-kp-notify-phone]');
      this.submit = this.querySelector('[data-kp-notify-submit]');
      this.message = this.querySelector('[data-kp-notify-message]');
      this.done = this.querySelector('[data-kp-notify-done]');
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.send();
      });

      this.watchButton();
      this.restore();
      this.arrive();
    }

    /*
     * The product card's "Notify me" row links here as `#KpNotify`. The
     * browser's own fragment jump lands the panel flush under the sticky
     * header — kp-notify-me.css's scroll-margin-top is what makes it clear
     * the header instead — and then does nothing else. So on arrival the
     * panel is checked to be in view, the email field takes focus (the one
     * thing the visitor came to do), and the frame's edge lights up once so
     * the eye finds the panel on a page it has just landed in. A panel that
     * is hidden because the cue is in stock after all is left alone: the
     * buy button is the answer then.
     */
    arrive() {
      if (!this.id || location.hash !== `#${this.id}` || this.hidden || !this.email) return;
      const go = () => {
        if (this.hidden) return;
        const margin = parseFloat(getComputedStyle(this).scrollMarginTop) || 0;
        const box = this.getBoundingClientRect();
        if (box.top < margin || box.bottom > window.innerHeight) {
          this.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
        this.email.focus({ preventScroll: true });
        this.classList.add('kp-notify--arrived');
        this.addEventListener('animationend', () => this.classList.remove('kp-notify--arrived'), { once: true });
      };
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /* ── Visibility follows Dawn's submit button ─────────────────────── */

    watchButton() {
      this.button = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      if (!this.button) return;

      this.observer = new MutationObserver(() => this.follow());
      this.observer.observe(this.button, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
      this.follow();
    }

    follow() {
      // Dawn writes the label into the button's FIRST span (product-form.js
      // does the same read); the whole textContent also carries the
      // spinner's visually-hidden "Loading" text.
      const label = (this.button.querySelector('span') || this.button).textContent.trim().toLowerCase();
      const soldOut = this.button.hasAttribute('disabled') && label === (this.dataset.soldOutLabel || '').trim().toLowerCase();
      this.hidden = !soldOut;
      if (soldOut) this.restore();
    }

    /* ── The variant, read from Dawn's form at the moment it matters ──── */

    variantId() {
      const input = document.querySelector(`#product-form-${this.dataset.section} .product-variant-id, .product-variant-id`);
      return input?.value || '';
    }

    /* ── Remember a signup per variant, so a return visit sees the state ── */

    restore() {
      const id = this.variantId();
      let signed = false;
      try {
        signed = !!id && localStorage.getItem(STORAGE_PREFIX + id) === '1';
      } catch (error) {
        /* storage blocked — the form simply shows again */
      }
      this.form.hidden = signed;
      this.done.hidden = !signed;
    }

    remember(id) {
      try {
        localStorage.setItem(STORAGE_PREFIX + id, '1');
      } catch (error) {
        /* storage blocked — nothing to do */
      }
    }

    /* ── Submit ────────────────────────────────────────────────────────── */

    async send() {
      this.say('');

      // A filled honeypot is a bot: pretend, send nothing.
      if (this.querySelector('[data-kp-notify-hp]')?.value) {
        this.finish(this.variantId());
        return;
      }

      const email = (this.email.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.email.setAttribute('aria-invalid', 'true');
        this.email.focus();
        return this.say(this.dataset.msgInvalid, true);
      }
      this.email.removeAttribute('aria-invalid');

      const phone = this.phone ? KpNotifyMe.toE164(this.phone.value) : '';
      if (this.phone && this.phone.value.trim() && !phone) {
        this.phone.setAttribute('aria-invalid', 'true');
        this.phone.focus();
        return this.say(this.dataset.msgInvalid, true);
      }

      if (!this.dataset.companyId) return this.say(this.dataset.msgUnconfigured, true);

      const variant = this.variantId();
      this.busy(true);
      try {
        const results = await Promise.all([
          this.post(BACK_IN_STOCK_URL, KpNotifyMe.backInStockPayload(variant, email, phone)),
          this.dataset.listId ? this.post(SUBSCRIPTIONS_URL, KpNotifyMe.subscriptionPayload(this.dataset.listId, email, phone)) : true,
        ]);
        if (!results.every(Boolean)) throw new Error('Klaviyo refused the signup');
        this.finish(variant);
      } catch (error) {
        this.say(this.dataset.msgFailed, true);
      } finally {
        this.busy(false);
      }
    }

    post(url, payload) {
      return fetch(`${url}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: API_REVISION },
        body: JSON.stringify(payload),
      }).then((response) => response.ok || response.status === 202 || response.status === 409);
    }

    finish(variant) {
      if (variant) this.remember(variant);
      this.form.hidden = true;
      this.done.hidden = false;
    }

    busy(on) {
      this.submit.toggleAttribute('disabled', on);
      this.submit.setAttribute('aria-busy', String(on));
      this.submit.querySelector('.kp-notify__submit-label').hidden = on;
      this.submit.querySelector('.kp-loader').hidden = !on;
    }

    say(text, error = false) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.hidden = !text;
      this.message.classList.toggle('kp-notify__message--error', !!text && error);
    }

    /* ── Payloads, as Klaviyo's Client API shapes them ─────────────────── */

    static backInStockPayload(variant, email, phone) {
      const profile = { email };
      const channels = ['EMAIL'];
      if (phone) {
        profile.phone_number = phone;
        channels.push('SMS');
      }
      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: { channels, profile: { data: { type: 'profile', attributes: profile } } },
          relationships: { variant: { data: { type: 'catalog-variant', id: VARIANT_ID_PREFIX + variant } } },
        },
      };
    }

    /*
     * Consent is named channel by channel. An empty `subscriptions` makes
     * Klaviyo subscribe every channel it has an identifier for — SMS would
     * switch on just because a number was typed.
     */
    static subscriptionPayload(listId, email, phone) {
      const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      const attributes = { email, subscriptions };
      if (phone) {
        attributes.phone_number = phone;
        subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }
      return {
        data: {
          type: 'subscription',
          attributes: { custom_source: 'Back in stock', profile: { data: { type: 'profile', attributes } } },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      };
    }

    /* US-first: ten digits get +1; anything already international is kept. */
    static toE164(raw) {
      const digits = (raw || '').replace(/\D/g, '');
      if (!digits) return '';
      if (raw.trim().startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`;
      if (digits.length === 10) return `+1${digits}`;
      if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
      return '';
    }
  }

  if (!customElements.get('kp-notify-me')) {
    customElements.define('kp-notify-me', KpNotifyMe);
  }
})();
